// src/pages/MobileCustomerFlowPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import CustomerFlow from '../components/CustomerFlow/CustomerFlow';
import { trackMobileCustomerFlowStarted } from '../analytics/events';

const MobileCustomerFlowPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  
  // Establecer el título de la página
  usePageTitle('Autogestión');
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowData, setFlowData] = useState(null);
  const [flowStatus, setFlowStatus] = useState('active');
  const [flowStatusLabel, setFlowStatusLabel] = useState('');
  const [stepStatuses, setStepStatuses] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Inicialización
  useEffect(() => {
    initializeFlow();
  }, []);

  const initializeFlow = async () => {
    try {
      await fetchFlowStatus();
      // CustomerFlow se encargará de determinar el paso actual
    } catch (error) {
      console.error('Error initializing flow:', error);
      setError(error.message || 'Este enlace ya no está disponible. Pedile a tu vendedor un nuevo enlace para continuar');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlowStatus = async () => {
    try {
      // Obtener información del localStorage (con prefijo temp_)
      const tokenCustomerId = localStorage.getItem('temp_tokenCustomerId');
      const tokenPurchaseRequestId = localStorage.getItem('temp_tokenPurchaseRequestId');

      if (!tokenCustomerId || !tokenPurchaseRequestId) {
        throw new Error('Información de token inválida o expirada');
      }

      const response = await api.get(
        `/customers/${tokenCustomerId}/purchase-requests/${tokenPurchaseRequestId}/self-service/status`
      );

      const { flow_id, status, status_label, steps, expires_at, flow_config } = response.data.data;

      // Verificar el estado del proceso antes de continuar
      if (status !== 'active') {
        let errorMessage = '';
        
        switch (status) {
          case 'completed':
            errorMessage = 'Este proceso ya fue completado, consulte a su vendedor para mas información';
            break;
          case 'expired':
            errorMessage = 'Este enlace ya no está disponible. Pedile a tu vendedor un nuevo enlace para continuar';
            break;
          case 'cancelled':
            errorMessage = 'Este proceso fue cancelado, consulte a su vendedor para mas información';
            break;
          default:
            errorMessage = `Estado del proceso no válido: ${status}. Consulte a su vendedor para mas información`;
        }
        
        throw new Error(errorMessage);
      }

      // Actualizar estados del proceso
      setFlowStatus(status);
      setFlowStatusLabel(status_label);
      setFlowData({ flow_id, tokenCustomerId, tokenPurchaseRequestId, flow_config });
      setStepStatuses(steps || {});
      
      // Track mobile customer flow started
      // Get branchId from localStorage (set by MobileProtectedRoute)
      const branchId = localStorage.getItem('temp_tokenBranchId');
      // Try to get commerceId from purchase request data if available
      // For now, we'll need to fetch it or get it from the API response
      // Since we don't have it directly, we'll track without it for now
      // The commerceId can be added later if available in the API response
      trackMobileCustomerFlowStarted({
        commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId
        branchId: branchId || undefined,
        flowId: flow_id
      });
      
      // Calcular tiempo restante
      if (expires_at) {
        const expiresDate = new Date(expires_at);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresDate - now) / 1000));
        setTimeRemaining(remaining);
      }
      
    } catch (error) {
      console.error('Error fetching flow status:', error);
      throw new Error(error.message || 'No se pudo obtener el estado del proceso');
    }
  };

  /**
   * Verifica el estado del flujo y lo interrumpe si es necesario
   */
  const checkFlowStatus = async () => {
    try {
      const { tokenCustomerId, tokenPurchaseRequestId } = flowData;
      
      const response = await api.get(
        `/customers/${tokenCustomerId}/purchase-requests/${tokenPurchaseRequestId}/self-service/status`
      );

      const { status } = response.data.data;
      
      // Si el estado cambió y ya no es activo, interrumpir el flujo
      // PERO no mostrar error si el flujo se completó exitosamente
      if (status !== 'active') {
        // Si el flujo se completó exitosamente, no mostrar error
        if (status === 'completed') {
          // El flujo se completó correctamente, no es un error
          return false; // Retornar false para que el flujo se detenga
        }
        
        // Para otros estados no válidos, mostrar el mensaje de error
        let errorMessage = '';
        
        switch (status) {
          case 'expired':
            errorMessage = 'Este enlace ya no está disponible. Pedile a tu vendedor un nuevo enlace para continuar';
            break;
          case 'cancelled':
            errorMessage = 'Este proceso fue cancelado, consulte a su vendedor para mas información';
            break;
          default:
            errorMessage = `Estado del proceso no válido: ${status}. Consulte a su vendedor para mas información`;
        }
        
        setError(errorMessage);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking flow status:', error);
      setError('No se pudo verificar el estado del proceso. Consulte a su vendedor para mas información');
      return false;
    }
  };

  /**
   * Actualiza el estado de un paso específico
   * @param {Object} stepData - Datos del paso incluyendo step_name y step_status
   */
  const handleStepStatus = async (stepData) => {
    try {
      // Verificar el estado del flujo antes de procesar el paso
      const isFlowActive = await checkFlowStatus();
      if (!isFlowActive) {
        return; // El flujo ya fue interrumpido por checkFlowStatus
      }

      const { tokenCustomerId, tokenPurchaseRequestId } = flowData;
      const { step_name, step_status = 'completed' } = stepData;
      
      // Llamar a la API para actualizar el estado del paso
      const response = await api.post(
        `/customers/${tokenCustomerId}/purchase-requests/${tokenPurchaseRequestId}/self-service/steps/${step_name}/${step_status}`,
        {
          step_data: stepData
        }
      );

      // Verificar que la respuesta sea exitosa
      if (response.data.success) {
        // Actualizar el estado local con la respuesta REAL de la API
        const apiStepStatus = response.data.data.status;
        const apiStepName = response.data.data.step_name;
        
        console.log(`API actualizó ${apiStepName} a estado: ${apiStepStatus}`);
        
        setStepStatuses(prev => ({
          ...prev,
          [apiStepName]: apiStepStatus
        }));

        // Si la API devuelve un estado diferente al enviado, loggear la diferencia
        if (apiStepStatus !== step_status) {
          console.log(`Estado enviado: ${step_status}, Estado devuelto por API: ${apiStepStatus}`);
        }
      } else {
        console.error('API no devolvió success:', response.data);
      }

      // CustomerFlow se encargará de recalcular el paso actual

    } catch (error) {
      console.error('Error updating step status:', error);
      // Aquí podrías mostrar un toast o notificación de error
    }
  };

  const handleStepBack = () => {
    // Lógica para retroceder al paso anterior si es necesario
    console.log('Step back requested');
  };

  if (loading) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted">Inicializando proceso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationCircle} size="3x" className="text-danger mb-3" />
          <h4>Atención</h4>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-8">
          <div className="card shadow-sm border-0 mt-3">
            <div className="card-body p-0">
              <CustomerFlow
                flowData={flowData}
                flowStatus={flowStatus}
                stepStatuses={stepStatuses}
                timeRemaining={timeRemaining}
                onStepComplete={handleStepStatus}
                onStepBack={handleStepBack}
                searchParams={searchParams}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCustomerFlowPage;
